import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DownloadOutlined, EditOutlined } from "@ant-design/icons";
import { Segmented, Select, Spin, message } from "antd";
import { Check, X } from "react-feather";

import AppContext from "../../../context/AppContext";
import { SIDER_TABS } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";
import {
  buildChangedLeagueLimitsPayload,
  buildChangedSportLimitsPayload,
  mergeLeagueLimitRows,
  useSgpLeagueLimits,
  useSgpSportLimits,
  useSgpSportsCountriesTree,
  useUpdateSgpLeagueLimits,
  useUpdateSgpSportLimits,
} from "../../../queries/sportsbook/sgpLimits";

import SGPLimitsTable from "./SGPLimitsTable";
import {
  SGP_LIMITS_BY_LEAGUE_PANEL_KEY,
  SGP_LIMITS_BY_SPORT_PANEL_KEY,
  getSgpLimitsPanels,
  SGP_LIMITS_TABS,
  SGP_LIMITS_TEST_IDS,
} from "./sgpLimitsTableData";
import styles from "./SGPLimits.module.css";

const SGP_SPORT_LIMITS_ERROR_MESSAGE =
  "Could not load sport limits. Try again or contact support.";
const SGP_SPORT_LIMITS_SAVE_SUCCESS_MESSAGE = "Sport limits saved successfully.";
const SGP_SPORT_LIMITS_SAVE_ERROR_MESSAGE =
  "Could not save sport limits. Try again or contact support.";
const SGP_SPORT_LIMITS_NO_CHANGES_MESSAGE = "No sport limit changes to save.";
const SGP_LEAGUE_TREE_ERROR_MESSAGE =
  "Could not load sport and country filters. Try again or contact support.";
const SGP_LEAGUE_LIMITS_ERROR_MESSAGE =
  "Could not load league limits. Try again or contact support.";
const SGP_LEAGUE_LIMITS_SAVE_SUCCESS_MESSAGE = "League limits saved successfully.";
const SGP_LEAGUE_LIMITS_SAVE_ERROR_MESSAGE =
  "Could not save league limits. Try again or contact support.";
const SGP_LEAGUE_LIMITS_NO_CHANGES_MESSAGE = "No league limit changes to save.";
const SGP_LEAGUE_LIMIT_RESET_SUCCESS_MESSAGE = "League default limit reset successfully.";
const SGP_LEAGUE_LIMIT_RESET_ERROR_MESSAGE =
  "Could not reset league default limit. Try again or contact support.";

const SEARCHABLE_FILTER_SELECT_PROPS = {
  showSearch: true,
  optionFilterProp: "label",
  virtual: false,
  popupClassName: "sgp-limits-filter-select-dropdown",
};

function parseCurrencyValue(value) {
  const normalized = String(value ?? "").replace(/[^0-9.]/g, "");
  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : 0;
}

function getPanelTestId(panelKey) {
  return panelKey === SGP_LIMITS_BY_SPORT_PANEL_KEY
    ? SGP_LIMITS_TEST_IDS.sportPanel
    : SGP_LIMITS_TEST_IDS.leaguePanel;
}

function SGPLimits() {
  usePageTitle(AppStrings.SPORTSBOOK_SGP_LIMITS);
  const { setActiveSidebarTab } = useContext(AppContext);
  const currencySymbol = useCurrencySymbol();
  const [activeTab, setActiveTab] = useState(SGP_LIMITS_BY_SPORT_PANEL_KEY);
  const [selectedSportId, setSelectedSportId] = useState(null);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [editingPanelKey, setEditingPanelKey] = useState(null);
  const [draftRows, setDraftRows] = useState([]);
  const [clearingLeagueId, setClearingLeagueId] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const isLeagueTabActive = activeTab === SGP_LIMITS_BY_LEAGUE_PANEL_KEY;

  const {
    data: sportLimitRows = [],
    isLoading: isSportLimitsLoading,
    isError: isSportLimitsError,
  } = useSgpSportLimits();
  const { mutate: saveSportLimits, isPending: isSavingSportLimits } =
    useUpdateSgpSportLimits();
  const { mutate: saveLeagueLimits, isPending: isSavingLeagueLimits } =
    useUpdateSgpLeagueLimits(selectedSportId, selectedCountryId);
  const {
    data: sportsCountriesTree = [],
    isLoading: isSportsCountriesTreeLoading,
    isError: isSportsCountriesTreeError,
  } = useSgpSportsCountriesTree(isLeagueTabActive);
  const {
    data: leagueLimitRows = [],
    isLoading: isLeagueLimitsLoading,
    isError: isLeagueLimitsError,
  } = useSgpLeagueLimits(selectedSportId, selectedCountryId, isLeagueTabActive);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.SPORTSBOOK, "SPORTSBOOK_SGP_LIMITS");
  }, [setActiveSidebarTab]);

  useEffect(() => {
    if (isSportLimitsError) {
      messageApi.error(SGP_SPORT_LIMITS_ERROR_MESSAGE);
    }
  }, [isSportLimitsError, messageApi]);

  useEffect(() => {
    if (isSportsCountriesTreeError) {
      messageApi.error(SGP_LEAGUE_TREE_ERROR_MESSAGE);
    }
  }, [isSportsCountriesTreeError, messageApi]);

  useEffect(() => {
    if (isLeagueLimitsError) {
      messageApi.error(SGP_LEAGUE_LIMITS_ERROR_MESSAGE);
    }
  }, [isLeagueLimitsError, messageApi]);

  const handleCancelEditing = useCallback(() => {
    setEditingPanelKey(null);
    setDraftRows([]);
  }, []);

  useEffect(() => {
    if (!isLeagueTabActive || sportsCountriesTree.length === 0) {
      return;
    }

    const selectedSportStillExists = sportsCountriesTree.some(
      (sport) => sport.sport_id === selectedSportId,
    );

    if (selectedSportStillExists) {
      const sportNode = sportsCountriesTree.find(
        (sport) => sport.sport_id === selectedSportId,
      );
      const selectedCountryStillExists = sportNode?.countries.some(
        (country) => country.country_id === selectedCountryId,
      );

      if (selectedCountryStillExists) {
        return;
      }

      handleCancelEditing();
      setSelectedCountryId(sportNode?.countries[0]?.country_id ?? null);
      return;
    }

    const firstSport = sportsCountriesTree[0];
    handleCancelEditing();
    setSelectedSportId(firstSport.sport_id);
    setSelectedCountryId(firstSport.countries[0]?.country_id ?? null);
  }, [
    handleCancelEditing,
    isLeagueTabActive,
    selectedCountryId,
    selectedSportId,
    sportsCountriesTree,
  ]);

  const sportFilterOptions = useMemo(
    () =>
      sportsCountriesTree.map((sport) => ({
        value: sport.sport_id,
        label: sport.sport_name,
      })),
    [sportsCountriesTree],
  );

  const countryFilterOptions = useMemo(() => {
    const selectedSport = sportsCountriesTree.find(
      (sport) => sport.sport_id === selectedSportId,
    );

    return (
      selectedSport?.countries.map((country) => ({
        value: country.country_id,
        label: country.country_name,
      })) ?? []
    );
  }, [selectedSportId, sportsCountriesTree]);

  const getPanelRows = useCallback(
    (panelKey) => {
      if (panelKey === SGP_LIMITS_BY_SPORT_PANEL_KEY) {
        return sportLimitRows;
      }
      if (panelKey === SGP_LIMITS_BY_LEAGUE_PANEL_KEY) {
        return leagueLimitRows;
      }
      return [];
    },
    [leagueLimitRows, sportLimitRows],
  );

  const handleStartEditing = (panelKey) => {
    setEditingPanelKey(panelKey);
    setDraftRows(getPanelRows(panelKey).map((row) => ({ ...row })));
  };

  const handleTabChange = (nextTab) => {
    setActiveTab(nextTab);
    handleCancelEditing();

    if (nextTab !== SGP_LIMITS_BY_LEAGUE_PANEL_KEY) {
      setSelectedSportId(null);
      setSelectedCountryId(null);
    }
  };

  const handleSportFilterChange = (sportId) => {
    const sportNode = sportsCountriesTree.find((sport) => sport.sport_id === sportId);

    setSelectedSportId(sportId);
    setSelectedCountryId(sportNode?.countries[0]?.country_id ?? null);
    handleCancelEditing();
  };

  const handleCountryFilterChange = (countryId) => {
    setSelectedCountryId(countryId);
    handleCancelEditing();
  };

  const handleSaveLeaguePanel = () => {
    if (!selectedSportId) {
      return;
    }

    const limits = buildChangedLeagueLimitsPayload(
      selectedSportId,
      leagueLimitRows,
      draftRows,
      parseCurrencyValue,
    );

    if (limits.length === 0) {
      messageApi.info(SGP_LEAGUE_LIMITS_NO_CHANGES_MESSAGE);
      setEditingPanelKey(null);
      setDraftRows([]);
      return;
    }

    saveLeagueLimits(
      { limits },
      {
        onSuccess: () => {
          messageApi.success(SGP_LEAGUE_LIMITS_SAVE_SUCCESS_MESSAGE);
          setEditingPanelKey(null);
          setDraftRows([]);
        },
        onError: () => {
          messageApi.error(SGP_LEAGUE_LIMITS_SAVE_ERROR_MESSAGE);
        },
      },
    );
  };

  const handleSaveSportLimitsPanel = () => {
    const limits = buildChangedSportLimitsPayload(
      sportLimitRows,
      draftRows,
      parseCurrencyValue,
    );

    if (limits.length === 0) {
      messageApi.info(SGP_SPORT_LIMITS_NO_CHANGES_MESSAGE);
      setEditingPanelKey(null);
      setDraftRows([]);
      return;
    }

    saveSportLimits(
      { limits },
      {
        onSuccess: () => {
          messageApi.success(SGP_SPORT_LIMITS_SAVE_SUCCESS_MESSAGE);
          setEditingPanelKey(null);
          setDraftRows([]);
        },
        onError: () => {
          messageApi.error(SGP_SPORT_LIMITS_SAVE_ERROR_MESSAGE);
        },
      },
    );
  };

  const handleSaveEditing = (panelKey) => {
    if (panelKey === SGP_LIMITS_BY_SPORT_PANEL_KEY) {
      handleSaveSportLimitsPanel();
      return;
    }
    if (panelKey === SGP_LIMITS_BY_LEAGUE_PANEL_KEY) {
      handleSaveLeaguePanel();
    }
  };

  const handleDraftFieldChange = (rowId, field, value) => {
    setDraftRows((current) =>
      current.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)),
    );
  };

  const applyLeagueLimitUpdatesToDraft = useCallback((updatedRows) => {
    if (!updatedRows?.length) {
      return;
    }

    setDraftRows((current) => mergeLeagueLimitRows(current, updatedRows));
  }, []);

  const handleClearLeagueDefaultLimit = (row) => {
    if (!selectedSportId || !row?.leagueId) {
      return;
    }

    setClearingLeagueId(row.id);
    saveLeagueLimits(
      {
        limits: [
          {
            sport_id: selectedSportId,
            league_id: row.leagueId,
            limit: null,
          },
        ],
      },
      {
        onSuccess: (updatedRows) => {
          messageApi.success(SGP_LEAGUE_LIMIT_RESET_SUCCESS_MESSAGE);
          setClearingLeagueId(null);

          if (updatedRows?.length) {
            applyLeagueLimitUpdatesToDraft(updatedRows);
          }
        },
        onError: () => {
          messageApi.error(SGP_LEAGUE_LIMIT_RESET_ERROR_MESSAGE);
          setClearingLeagueId(null);
        },
      },
    );
  };

  const panels = useMemo(
    () => getSgpLimitsPanels(currencySymbol),
    [currencySymbol],
  );
  const activePanel = useMemo(
    () => panels.find((panel) => panel.key === activeTab),
    [activeTab, panels],
  );

  if (!activePanel) {
    return null;
  }

  const {
    key,
    title,
    columns,
    showEditButton,
    editableField,
    editableValueType,
  } = activePanel;
  const isEditing = editingPanelKey === key;
  const isSportPanel = key === SGP_LIMITS_BY_SPORT_PANEL_KEY;
  const isLeaguePanel = key === SGP_LIMITS_BY_LEAGUE_PANEL_KEY;
  const rows = isEditing ? draftRows : getPanelRows(key);
  const isPanelLoading = isSportPanel
    ? isSportLimitsLoading
    : isSportsCountriesTreeLoading || isLeagueLimitsLoading;
  const isPanelSaving =
    (isSportPanel && isSavingSportLimits) || (isLeaguePanel && isSavingLeagueLimits);
  const isLeagueEditDisabled =
    isLeaguePanel &&
    (isSportsCountriesTreeLoading ||
      isLeagueLimitsLoading ||
      !selectedSportId ||
      !selectedCountryId);

  return (
    <>
      <div className={styles["sgp-limits-page"]} data-testid="sgp-limits">
        {contextHolder}
        <div className={styles["sgp-limits-header"]}>
          <div className={styles["sgp-limits-header-actions"]}>
            <button
              type="button"
              className={styles["sgp-limits-download-button"]}
              aria-label="Download"
            >
              <DownloadOutlined />
            </button>
          </div>
        </div>

        <div className={styles["sgp-limits-toolbar"]}>
          <Segmented
            className={styles["sgp-limits-tabs"]}
            value={activeTab}
            onChange={handleTabChange}
            options={SGP_LIMITS_TABS}
          />
        </div>

        <section
          className={styles["sgp-limits-panel"]}
          aria-label={title}
          data-testid={getPanelTestId(key)}
        >
          <div className={styles["sgp-limits-panel-top"]}>
            <div className={styles["sgp-limits-panel-title-row"]}>
              <div className={styles["sgp-limits-panel-actions"]}>
                {isLeaguePanel ? (
                  <>
                    <Select
                      {...SEARCHABLE_FILTER_SELECT_PROPS}
                      className={styles["sgp-limits-filter-select"]}
                      value={selectedSportId ?? undefined}
                      placeholder={AppStrings.SPORT}
                      options={sportFilterOptions}
                      onChange={handleSportFilterChange}
                      aria-label={AppStrings.SPORT}
                      bordered={false}
                      loading={isSportsCountriesTreeLoading}
                      disabled={isSportsCountriesTreeLoading || sportFilterOptions.length === 0}
                    />
                    <Select
                      {...SEARCHABLE_FILTER_SELECT_PROPS}
                      className={styles["sgp-limits-filter-select"]}
                      value={selectedCountryId ?? undefined}
                      placeholder={AppStrings.COUNTRY}
                      options={countryFilterOptions}
                      onChange={handleCountryFilterChange}
                      aria-label={AppStrings.COUNTRY}
                      bordered={false}
                      loading={isSportsCountriesTreeLoading}
                      disabled={
                        isSportsCountriesTreeLoading ||
                        countryFilterOptions.length === 0
                      }
                      notFoundContent={
                        selectedSportId
                          ? "No countries available for this sport"
                          : "Select a sport first"
                      }
                    />
                  </>
                ) : null}
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      className={[
                        styles["sgp-limits-panel-action-button"],
                        styles["sgp-limits-panel-action-button--cancel"],
                      ].join(" ")}
                      aria-label={`Cancel ${title}`}
                      onClick={handleCancelEditing}
                      disabled={isPanelSaving}
                    >
                      <X size={16} />
                    </button>
                    <button
                      type="button"
                      className={[
                        styles["sgp-limits-panel-action-button"],
                        styles["sgp-limits-panel-action-button--save"],
                      ].join(" ")}
                      aria-label={`Save ${title}`}
                      onClick={() =>
                        handleSaveEditing(key)
                      }
                      disabled={isPanelSaving}
                    >
                      <Check size={16} />
                    </button>
                  </>
                ) : null}
                {!isEditing && showEditButton ? (
                  <button
                    type="button"
                    className={[
                      styles["sgp-limits-panel-action-button"],
                      styles["sgp-limits-panel-action-button--edit"],
                    ].join(" ")}
                    aria-label={`Edit ${title}`}
                    onClick={() => handleStartEditing(key)}
                    disabled={
                      (isSportPanel && isSportLimitsLoading) || isLeagueEditDisabled
                    }
                  >
                    <EditOutlined />
                  </button>
                ) : null}
              </div>
            </div>
            <div className={styles["sgp-limits-panel-divider"]} />
          </div>
          <div className={styles["sgp-limits-inner-panel"]}>
            {isPanelLoading ? (
              <div
                className={styles["sgp-limits-panel-loading"]}
                data-testid={`${key}-loading`}
              >
                <Spin />
              </div>
            ) : (
              <SGPLimitsTable
                tableKey={key}
                columns={columns}
                rows={rows}
                isEditing={isEditing}
                editableField={editableField}
                editableValueType={editableValueType}
                onFieldChange={handleDraftFieldChange}
                currencySymbol={currencySymbol}
                onClearLeagueLimit={
                  isLeaguePanel && isEditing ? handleClearLeagueDefaultLimit : undefined
                }
                clearingLeagueId={clearingLeagueId}
              />
            )}
          </div>
        </section>
      </div>
    </>
  );
}

export default SGPLimits;
